// Resolves the assembly graph. One bundle serves every integration mode; the choice
// is made at RUNTIME from attributes, so the host never needs a special build:
//
//   • Static assets (default, simplest): the graph is a JSON file co-located with the
//     geometry under `asset-base` — host it on any CDN or their own server, no API.
//   • API mode (opt-in via `api-base`): the graph is fetched from their backend, authed
//     by the embed key. Enables dynamic/per-tenant graphs + usage metering.
//
// Geometry (the .glb) is always served statically from `asset-base` — it's large and
// static, so it belongs on a CDN regardless of where the graph comes from.

import { AssemblyGraph } from "./types";
import { assertGraphStations } from "./renderState";

export interface LoadOptions {
  assemblyId: string;
  apiKey?: string;
  /** Base URL for static assets (glb, draco, and — in static mode — the graph JSON). */
  assetBase: string;
  /** When set, fetch the graph from `${apiBase}assembly/{id}` authed by the embed key. */
  apiBase?: string;
}

export async function loadAssemblyGraph(opts: LoadOptions): Promise<AssemblyGraph> {
  const { assemblyId, apiKey, assetBase, apiBase } = opts;

  const url = apiBase
    ? `${apiBase}assembly/${encodeURIComponent(assemblyId)}`
    : `${assetBase}${assemblyId}/assembly-graph.json`;

  const headers: Record<string, string> = {};
  if (apiBase && apiKey) headers["x-embed-key"] = apiKey;

  const res = await fetch(url, { headers });
  if (!res.ok) {
    throw new Error(`assembly-graph load failed (${res.status}) at ${url}`);
  }
  const graph = (await res.json()) as AssemblyGraph;
  assertGraphStations(graph);
  return graph;
}

/** The glb URL for an assembly — always static, from the asset base. */
export function glbUrl(assetBase: string, assemblyId: string): string {
  return `${assetBase}${assemblyId}/v12.glb`;
}
